// pages/Documents/EditDocuments.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import backendURL from "../../config";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { Button as AntButton, Modal as AntModal, Input, Space, Select, Dropdown, Menu } from 'antd';
import { DownOutlined } from '@ant-design/icons';

const { Option } = Select;

const ensureUrlProtocol = (url) => {
  if (!url) return '';
  if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url) && !/^tel:/i.test(url)) {
    return 'https://' + url;
  }
  return url;
};


export default function EditDocuments({ document, onClose, onUpdate }) {
  const [pageContent, setPageContent] = useState(document?.page_content || "<p></p>");
  const [websiteLink, setWebsiteLink] = useState(document?.website_link || "");
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableColumns, setTableColumns] = useState(3);
  const [toolbarSaveStatus, setToolbarSaveStatus] = useState('save');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Underline,
      Table.configure({ resizable: true }),
      TableCell, TableHeader, TableRow,
      Link.configure({
        openOnClick: false, // Important: disable default click behavior
        autolink: true, linkOnPaste: true,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
    ],
    content: pageContent,
    onUpdate: ({ editor }) => {
      // No need to set state here, we get content on save
    },
    editorProps: {
      attributes: { class: 'tiptap-editor-area p-3 form-control' },
    },
  });

  useEffect(() => {
    const initialContent = document?.page_content || "<p></p>";
    const initialLink = document?.website_link || "";

    setPageContent(initialContent);
    setWebsiteLink(initialLink);

    if (editor && editor.isEditable) {
      const currentEditorContent = editor.getHTML();
      if (currentEditorContent !== initialContent) {
        editor.commands.setContent(initialContent, false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document]);
  
  // <<< START: NEW CODE >>>
  // Handle Ctrl/Cmd + click on links to open in a new tab
  useEffect(() => {
    if (!editor || !editor.view.dom) return;

    const editorElement = editor.view.dom;
    const handleLinkClick = (event) => {
        const link = event.target.closest('a');
        if (link && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            window.open(link.href, '_blank', 'noopener, noreferrer');
        }
    };

    editorElement.addEventListener('click', handleLinkClick);

    return () => {
        editorElement.removeEventListener('click', handleLinkClick);
    };
  }, [editor]);
  // <<< END: NEW CODE >>>

  const setLinkCallback = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Enter URL for selected text:', previousUrl || '');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    const validatedUrl = ensureUrlProtocol(url);
    editor.chain().focus().extendMarkRange('link').setLink({ href: validatedUrl }).run();
  }, [editor]);


  const handleSave = async () => {
    if (!editor) return;
    
    const trimmedLink = websiteLink.trim();
    if (trimmedLink && !/^(https?:\/\/)/i.test(trimmedLink) && trimmedLink !== '#') {
        setError("Invalid Source Website Link. Must start with http:// or https://, be '#', or be empty.");
        return;
    }

    setIsSaving(true);
    setToolbarSaveStatus('saving');
    setError(null);

    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("Authentication token not found.");
      
      const headers = { Authorization: `Bearer ${token}` };
      const currentContentHtml = editor.getHTML();

      if (!document?.id) throw new Error("Document ID is missing.");
      
      const finalLink = trimmedLink ? trimmedLink : '#';
      const allDataPayload = {
          ...document, 
          page_content: currentContentHtml, 
          website_link: finalLink,
      };
      delete allDataPayload.user;

      const response = await axios.post(
        `${backendURL}/edit-private-data`,
        { 
            id: document.id, 
            page_content: currentContentHtml,
            all_data: allDataPayload
        },
        { headers }
      );

      if (response.status === 200 && response.data.success) {
        onUpdate();
        setToolbarSaveStatus('saved');
        setTimeout(() => {
          setToolbarSaveStatus('save');
          onClose(); 
        }, 1500);
      } else {
         throw new Error(response.data.message || `Failed to update document (Status: ${response.status})`);
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message || "Failed to update the document.";
      setError(message);
      setToolbarSaveStatus('save');
    } finally {
      setIsSaving(false);
    }
  };

  const insertTableDialog = useCallback(() => setIsTableDialogOpen(true), []);
  const closeTableDialog = useCallback(() => setIsTableDialogOpen(false), []);
  const handleInsertTable = useCallback(() => {
    if (!editor) return; const rows = parseInt(tableRows, 10); const cols = parseInt(tableColumns, 10);
    if (rows > 0 && cols > 0) { editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run(); }
    setIsTableDialogOpen(false);
  }, [editor, tableRows, tableColumns]);
  const handleTableRowsChange = useCallback((e) => setTableRows(Math.max(1, parseInt(e.target.value, 10) || 1)), []);
  const handleTableColumnsChange = useCallback((e) => setTableColumns(Math.max(1, parseInt(e.target.value, 10) || 1)), []);
  const handleAddColumn = useCallback(() => editor?.chain().focus().addColumnAfter().run(), [editor]);
  const handleAddRow = useCallback(() => editor?.chain().focus().addRowAfter().run(), [editor]);
  const handleDeleteColumn = useCallback(() => editor?.chain().focus().deleteColumn().run(), [editor]);
  const handleDeleteRow = useCallback(() => editor?.chain().focus().deleteRow().run(), [editor]);
  const handleDeleteTable = useCallback(() => editor?.chain().focus().deleteTable().run(), [editor]);
  const handleHeadingChange = useCallback((value) => {
    if (!editor) return; if (value === 'paragraph') { editor.chain().focus().setParagraph().run(); }
    else { const level = parseInt(value.slice(1), 10); if (level >= 1 && level <= 6) { editor.chain().focus().toggleHeading({ level }).run();}}
  }, [editor]);


  if (!editor) {
    return <div className="p-3">Loading editor...</div>;
  }

  const tableMenu = (
    <Menu>
      <Menu.Item key="insertTable" onClick={insertTableDialog}>Insert Table</Menu.Item>
      <Menu.Item key="addColumn" onClick={handleAddColumn} disabled={!editor.can().addColumnAfter()}>Add Column After</Menu.Item>
      <Menu.Item key="addRow" onClick={handleAddRow} disabled={!editor.can().addRowAfter()}>Add Row After</Menu.Item>
      <Menu.Item key="deleteColumn" onClick={handleDeleteColumn} disabled={!editor.can().deleteColumn()}>Delete Column</Menu.Item>
      <Menu.Item key="deleteRow" onClick={handleDeleteRow} disabled={!editor.can().deleteRow()}>Delete Row</Menu.Item>
      <Menu.Divider />
      <Menu.Item key="deleteTable" onClick={handleDeleteTable} disabled={!editor.can().deleteTable()}>Delete Table</Menu.Item>
    </Menu>
  );
  const currentHeadingLevel = () => {
    for (let i = 1; i <= 6; i++) { if (editor.isActive('heading', { level: i })) return `h${i}`; } return 'paragraph';
  };


  return (
    <div className="edit-documents-content d-flex flex-column" style={{height: '100%', overflow:'hidden', backgroundColor: '#fff'}}>
      <div className="modal-header py-2 px-3 border-bottom">
        <h5 className="modal-title">Edit Document: {document?.filename}</h5>
        <button type="button" className="btn-close" aria-label="Close" onClick={onClose} disabled={isSaving}></button>
      </div>
      
      <div className="tiptap-toolbar p-2 border-bottom bg-light sticky-top d-flex align-items-center flex-wrap">
          <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleBold().run()} type={editor.isActive('bold') ? 'primary' : 'default'} title="Bold"><i className="fa fa-bold"></i></AntButton>
          <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleItalic().run()} type={editor.isActive('italic') ? 'primary' : 'default'} title="Italic"><i className="fa fa-italic"></i></AntButton>
          <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleUnderline().run()} type={editor.isActive('underline') ? 'primary' : 'default'} title="Underline"><i className="fa fa-underline"></i></AntButton>
          <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleStrike().run()} type={editor.isActive('strike') ? 'primary' : 'default'} title="Strike"><i className="fa fa-strikethrough"></i></AntButton>
          <AntButton size="small" className="me-1" onClick={setLinkCallback} type={editor.isActive('link') ? 'primary' : 'default'} title="Link in Content"><i className="fa fa-link"></i></AntButton>
          <Select size="small" value={currentHeadingLevel()} style={{ width: 120, margin: '0 8px' , zIndex: 1400 }} onChange={handleHeadingChange} disabled={!editor.can().setParagraph() && !editor.can().toggleHeading({level: 1})}>
            <Option value="paragraph">Paragraph</Option><Option value="h1">H1</Option><Option value="h2">H2</Option><Option value="h3">H3</Option>
            <Option value="h4">H4</Option><Option value="h5">H5</Option><Option value="h6">H6</Option>
          </Select>
          <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleBulletList().run()} type={editor.isActive('bulletList') ? 'primary' : 'default'} title="Bullet List"><i className="fa fa-list-ul"></i></AntButton>
          <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().toggleOrderedList().run()} type={editor.isActive('orderedList') ? 'primary' : 'default'} title="Ordered List"><i className="fa fa-list-ol"></i></AntButton>
          <Dropdown overlay={tableMenu} trigger={['click']} overlayStyle={{ zIndex: 1500 }}>
            <AntButton size="small" className="me-1"><span><i className="fa fa-table" style={{ marginRight: '4px'}}></i> Table <DownOutlined /></span></AntButton>
          </Dropdown>
          <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><i className="fa fa-undo"></i></AntButton>
          <AntButton size="small" className="me-1" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><i className="fa fa-repeat"></i></AntButton>
          <AntButton size="small" onClick={handleSave} title="Save Changes" loading={isSaving || toolbarSaveStatus === 'saving'} 
                     icon={toolbarSaveStatus === 'saving' ? <i className="fa fa-spinner fa-spin"></i> : toolbarSaveStatus === 'saved' ? <i className="fa fa-check"></i> : <i className="fa fa-save"></i>}>
          </AntButton>
      </div>

      <div className="modal-body p-3" style={{flexGrow:1, overflowY:'auto'}}>
        {error && (
            <div className="alert alert-danger py-1 px-2 mb-2" role="alert" style={{fontSize: '0.85rem'}}>
                {error}
            </div>
        )}
        
        <div className="mb-3">
          <label htmlFor="websiteLinkEdit" className="form-label fw-bold">
            Source Website Link (Optional):
          </label>
          <Input
            id="websiteLinkEdit"
            type="url"
            value={websiteLink}
            onChange={(e) => setWebsiteLink(e.target.value)}
            placeholder="https://example.com/source-of-document or #"
            addonBefore={<i className="fa fa-globe"></i>}
            disabled={isSaving}
            allowClear
          />
           <small className="text-muted d-block mt-1">
                If this document originated from a specific URL, you can record it here. Use '#' or leave blank for none.
            </small>
        </div>
        
        <div className="mb-3">
          <label htmlFor="tiptap-editor-content" className="form-label fw-bold">Document Content:</label>
          <EditorContent editor={editor} id="tiptap-editor-content" />
        </div>
        
      </div>

      <div className="modal-footer py-2 px-3 border-top">
          <AntButton onClick={onClose} disabled={isSaving} icon={<i className="fa fa-times" style={{ marginRight: '4px' }}></i>}>
             Cancel
          </AntButton>
          <AntButton type="primary" onClick={handleSave} loading={isSaving || toolbarSaveStatus === 'saving'} 
                  icon={toolbarSaveStatus === 'saving' ? <i className="fa fa-spinner fa-spin"></i> : toolbarSaveStatus === 'saved' ? <i className="fa fa-check"></i> : <i className="fa fa-save"></i>}
                  style={{minWidth: '100px'}}>
            {toolbarSaveStatus === 'saving' ? 'Saving...' : toolbarSaveStatus === 'saved' ? 'Saved!' : 'Save'}
          </AntButton>
      </div>
      
      <AntModal title="Insert Table" open={isTableDialogOpen} onOk={handleInsertTable} onCancel={closeTableDialog} okText="Insert" cancelText="Cancel" zIndex={1500}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input type="number" addonBefore="Rows" value={tableRows} onChange={handleTableRowsChange} min={1} />
          <Input type="number" addonBefore="Columns" value={tableColumns} onChange={handleTableColumnsChange} min={1} />
        </Space>
      </AntModal>
      
      <style jsx global>{`
        .edit-documents-content .tiptap-toolbar { z-index: 10; }
        .edit-documents-content .tiptap-editor-area { min-height: 250px; max-height: calc(90vh - 300px); overflow-y: auto; }
        .ProseMirror { padding: 0.5rem; }
        .ProseMirror p { margin-bottom: 0.5rem; line-height: 1.5; }
        .ProseMirror ul, .ProseMirror ol { padding-left: 1.5rem; margin-bottom: 0.5rem; }
        .ProseMirror a { color: #0d6efd; text-decoration: underline; cursor: pointer; }
        .ProseMirror table { border-collapse: collapse; margin: 0.5rem 0; width: 100%; }
        .ProseMirror th, .ProseMirror td { border: 1px solid #dee2e6; padding: 0.3rem 0.4rem; vertical-align: top; }
        .ProseMirror th { font-weight: bold; text-align: left; background-color: #f1f3f5; }
        .ant-select-dropdown, .ant-dropdown-menu { z-index: 1350 !important; }
        .ant-modal-root { z-index: 1400 !important; }
       `}</style>
    </div>
  );
}